import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import {
  ArrowLeft,
  Shield,
  PenLine,
  Download,
  Copy,
  CheckCircle,
  Wrench,
  AlertCircle,
  UserSearch,
  FileQuestion,
  PoundSterling,
  Undo2,
  Sparkles,
  Loader2,
  RotateCcw,
} from "lucide-react";
import AnonymityBanner from "@/components/AnonymityBanner";
import NextStepsFooter from "@/components/NextStepsFooter";
import "@/styles/govtech.css";

const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

const LETTER_TYPES = [
  {
    id: "repair_request",
    label: "Repair Request",
    description: "Ask the landlord to fix something in your home",
    icon: Wrench,
    example: "e.g. My boiler stopped working two weeks ago. I've phoned twice but no engineer has visited.",
  },
  {
    id: "formal_complaint",
    label: "Formal Complaint",
    description: "Raise a Stage 1 complaint about poor service",
    icon: AlertCircle,
    example: "e.g. My repair request has been ignored for 6 weeks and no one has returned my calls.",
  },
  {
    id: "housing_officer_contact",
    label: "Who Is My Housing Officer?",
    description: "Ask for the name and direct contact details of your housing officer",
    icon: UserSearch,
    example: "e.g. I don't know who my housing officer is and I need to speak to someone about my tenancy.",
  },
  {
    id: "information_request",
    label: "Information Request",
    description: "Ask for documents or information you are entitled to see",
    icon: FileQuestion,
    example: "e.g. I would like a copy of my rent statement for the last 12 months.",
  },
  {
    id: "rent_query",
    label: "Rent Query",
    description: "Ask the landlord to explain a charge or review a rent increase",
    icon: PoundSterling,
    example: "e.g. My rent has gone up by £25 a week but I haven't received a written explanation.",
  },
  {
    id: "appeal_decision",
    label: "Appeal a Decision",
    description: "Respectfully ask the landlord to reconsider a decision",
    icon: Undo2,
    example: "e.g. My application for a mutual exchange has been refused and I'd like to appeal.",
  },
  {
    id: "other",
    label: "Something Else",
    description: "Any other polite, formal letter to your landlord",
    icon: PenLine,
    example: "e.g. Describe what you want the letter to say — in your own words.",
  },
];

const API = process.env.REACT_APP_BACKEND_URL;

export default function LetterWriter() {
  const navigate = useNavigate();
  const [step, setStep] = useState("pick"); // pick | describe | result
  const [selectedType, setSelectedType] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [letter, setLetter] = useState(null);
  const [letterLabel, setLetterLabel] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e) => {
    e?.preventDefault?.();
    if (!selectedType || description.trim().length < 10) {
      setError("Please tell us a little more about your situation (at least a short sentence).");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/letter-writer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          letter_type: selectedType.id,
          plain_english_description: description.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || "Something went wrong. Please try again.");
      }
      const data = await res.json();
      setLetter(data.letter);
      setLetterLabel(data.letter_type_label);
      setStep("result");
    } catch (err) {
      setError(err.message || "We couldn't draft your letter just now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(letter);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError("Couldn't copy automatically. Please select the text and copy it manually.");
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const marginX = 20;
    const marginY = 25;
    const maxWidth = 170;
    const lineHeight = 6;

    // Header stripe
    doc.setFillColor(0, 112, 60);
    doc.rect(0, 0, 210, 12, "F");

    // Footer brand (small)
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text("Letter drafted via TenURes.co.uk — your anonymous housing ally", marginX, 287);

    // Body
    doc.setFontSize(11);
    doc.setTextColor(30);
    const lines = doc.splitTextToSize(letter, maxWidth);
    let y = marginY;
    lines.forEach((line) => {
      if (y > 275) {
        doc.addPage();
        doc.setFillColor(0, 112, 60);
        doc.rect(0, 0, 210, 12, "F");
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("Letter drafted via TenURes.co.uk — your anonymous housing ally", marginX, 287);
        doc.setFontSize(11);
        doc.setTextColor(30);
        y = marginY;
      }
      doc.text(line, marginX, y);
      y += lineHeight;
    });

    doc.save(`TenURes_${letterLabel.replace(/\s+/g, "_")}_Letter.pdf`);
  };

  const handleStartOver = () => {
    setStep("pick");
    setSelectedType(null);
    setDescription("");
    setLetter(null);
    setLetterLabel("");
    setError("");
    setCopied(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate("/resident")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            data-testid="back-to-portal-btn"
          >
            <ArrowLeft size={20} />
            <span>Back to Portal</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00703C] bg-opacity-10 rounded-full text-xs font-medium text-[#00703C] mb-4">
            <Sparkles size={14} />
            AI-Assisted Letter Writer
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#333333] mb-3">
            Describe your issue in plain English.
            <br />
            <span className="text-[#00703C]">Walk away with a polished letter.</span>
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Tell us what's going on, in your own words. We'll turn it into a polite, formal UK-English letter
            you can print, email, or hand to your landlord.
          </p>
        </div>

        {/* Authority Stamp */}
        <div className="authority-stamp mb-6">
          <div className="authority-stamp-icon">
            <Shield size={20} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Written in Your Voice, Polished Like a Pro</p>
            <p className="text-sm text-gray-600">Polite but firm UK-English letters. No jargon. No threats.</p>
          </div>
        </div>

        <AnonymityBanner />

        {/* Step 1: Pick a letter type */}
        {step === "pick" && (
          <section data-testid="letter-type-picker">
            <h2 className="text-xl font-bold text-[#333333] mb-4">
              What kind of letter do you need?
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {LETTER_TYPES.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setSelectedType(t);
                      setStep("describe");
                      setError("");
                    }}
                    className="text-left bg-white border-2 border-gray-200 hover:border-[#00703C] hover:bg-[#00703C] hover:bg-opacity-5 transition-all p-4 rounded-md"
                    data-testid={`letter-type-${t.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                        <Icon className="text-[#00703C]" size={20} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#333333]">{t.label}</p>
                        <p className="text-xs text-gray-600 mt-1">{t.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 2: Describe */}
        {step === "describe" && selectedType && (
          <section data-testid="letter-describe">
            <button
              onClick={() => {
                setStep("pick");
                setError("");
              }}
              className="flex items-center gap-2 text-[#00703C] mb-4 hover:underline font-medium text-sm"
              data-testid="change-letter-type-btn"
            >
              <ArrowLeft size={16} /> Pick a different letter type
            </button>

            <div className="bg-white border border-gray-200 p-6 rounded-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center">
                  <selectedType.icon className="text-[#00703C]" size={20} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">You're writing a</p>
                  <h2 className="font-bold text-[#333333]">{selectedType.label}</h2>
                </div>
              </div>

              <form onSubmit={handleGenerate}>
                <label className="block text-sm font-semibold text-[#333333] mb-2">
                  Tell us what's going on, in your own words
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  placeholder={selectedType.example}
                  className="w-full border-2 border-gray-200 rounded-md p-3 text-gray-800 focus:border-[#00703C] focus:outline-none"
                  maxLength={2000}
                  data-testid="letter-description-input"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1 mb-4">
                  <span>No need for perfect spelling or grammar — we'll handle that.</span>
                  <span>{description.length}/2000</span>
                </div>

                <div className="bg-[#FFFBF2] border-l-4 border-[#F47738] p-3 rounded text-xs text-gray-700 mb-4">
                  <strong>Important:</strong> Don't include your real name, address, or any personal details
                  in your description. The letter will use clear placeholders like <code>[YOUR NAME]</code> and{" "}
                  <code>[YOUR ADDRESS]</code> that you fill in yourself before sending.
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded mb-4" data-testid="letter-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || description.trim().length < 10}
                  className={`govtech-btn govtech-btn-primary w-full ${
                    loading || description.trim().length < 10 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  data-testid="generate-letter-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Writing your letter...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} className="mr-2" />
                      Draft My Letter
                    </>
                  )}
                </button>
                {loading && (
                  <p className="text-xs text-gray-500 text-center mt-2">
                    This usually takes about 5–10 seconds. Hang tight.
                  </p>
                )}
              </form>
            </div>
          </section>
        )}

        {/* Step 3: Result */}
        {step === "result" && letter && (
          <section data-testid="letter-result">
            <div className="bg-white border border-gray-200 p-6 rounded-md">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-gray-500">Your draft</p>
                  <h2 className="font-bold text-[#333333]">{letterLabel}</h2>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-[#00703C] bg-opacity-10 rounded-full text-xs text-[#00703C] font-medium">
                  <CheckCircle size={14} /> Draft ready
                </div>
              </div>

              {/* Reassurance about placeholders */}
              <div className="bg-[#F3F8F5] border-l-4 border-[#00703C] p-3 rounded text-sm text-gray-800 mb-4">
                <p className="font-semibold text-[#00703C] mb-1">Before you send</p>
                <p>
                  Replace every <code>[PLACEHOLDER]</code> in the letter below with your real details (name,
                  address, dates, etc.). We don't know any of these — they stay with you.
                </p>
              </div>

              {/* Letter preview */}
              <div className="bg-gray-50 border border-gray-200 rounded p-5 mb-4 whitespace-pre-wrap font-mono text-sm text-gray-800 leading-relaxed max-h-[500px] overflow-y-auto" data-testid="letter-text">
                {letter}
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleDownloadPDF}
                  className="govtech-btn govtech-btn-primary flex-1"
                  data-testid="download-letter-btn"
                >
                  <Download size={18} className="mr-2" />
                  Download as PDF
                </button>
                <button
                  onClick={handleCopy}
                  className="govtech-btn govtech-btn-secondary flex-1"
                  data-testid="copy-letter-btn"
                >
                  {copied ? (
                    <>
                      <CheckCircle size={18} className="mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={18} className="mr-2" />
                      Copy to Clipboard
                    </>
                  )}
                </button>
              </div>

              <button
                onClick={handleStartOver}
                className="w-full mt-4 text-[#00703C] font-medium text-sm hover:underline flex items-center justify-center gap-2"
                data-testid="start-over-btn"
              >
                <RotateCcw size={16} /> Write a different letter
              </button>
            </div>

            {/* Next Steps / Call-Deflation */}
            <NextStepsFooter reviewDays={14} teamName="housing team" />
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            Letters are drafted by AI based on your words. Always read your letter carefully before sending.
            This is guidance, not legal advice.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            © {new Date().getFullYear()} Ten
            <span className="text-[#00703C] font-semibold">UR</span>es.co.uk
          </p>
        </div>
      </footer>
    </div>
  );
}
